import { Button, HFlow, Icon, PagedTable, Text, Tooltip } from 'bold-ui'
import { useAlert } from 'components/alert'
import CheckPermission from 'components/auth/CheckPermission'
import { DateTime } from 'components/date/DateTime'
import { useErrorHandler } from 'components/error'
import { confirm } from 'components/modals/confirm'
import { OrigemDadoEnumRecord } from 'components/origem-label-tag/OrigemRegistroInfo'
import { ButtonLink } from 'components/route'
import { usePagedTableProps } from 'components/table'
import { TableBox } from 'components/table/TableBox'
import { css } from 'emotion'
import {
  useAtividadeColetivaTableQuery,
  useExcluirAtividadeColetivaMutation,
  useFatoAtividadeColetivaTableQuery,
} from 'graphql/hooks.generated'
import { AtividadeColetivaQueryInput } from 'graphql/types.generated'
import { useFirebase } from 'hooks/firebase/useFirebase'
import React, { MouseEvent, useEffect, useState } from 'react'
import { useRouteMatch } from 'react-router'
import Permissions from 'types/Permissions'
import {
  AtividadeColetivaFonteEnum,
  AtividadeColetivaTableItem,
  tipoAtividadeRecord,
} from 'view/atividade-coletiva/model-atividadeColetiva'
import { formatNomeProfissional } from 'view/profissional/utils/utils-profissional'

import { convertFatoAtividadeColetivaTableQueryToAtividadeColetivaTableQuery } from '../converter-atividadeColetiva'

interface AtividadeColetivaTableProps {
  fonte: AtividadeColetivaFonteEnum
  filter: AtividadeColetivaQueryInput
}

export const AtividadeColetivaTable = (props: AtividadeColetivaTableProps) => {
  const { fonte, filter } = props
  const match = useRouteMatch()
  const handleRejection = useErrorHandler()
  const { analytics } = useFirebase()
  const alert = useAlert()
  const isFonteDadosPec = fonte === AtividadeColetivaFonteEnum.PEC
  const [excluirAtividadeColetiva] = useExcluirAtividadeColetivaMutation()
  const [localFilter, setLocalFilter] = useState(filter)

  useEffect(() => {
    setLocalFilter({
      ...filter,
      pageParams: { ...(filter.pageParams ?? {}), page: 0 },
    })
  }, [filter, fonte])

  const { data, loading, refetch } = useAtividadeColetivaTableQuery({
    variables: { input: localFilter },
    fetchPolicy: 'cache-and-network',
    skip: !isFonteDadosPec,
  })

  const { data: dataFato, loading: loadingFato } = useFatoAtividadeColetivaTableQuery({
    variables: { input: localFilter },
    fetchPolicy: 'cache-and-network',
    skip: isFonteDadosPec,
  })

  const tableProps = usePagedTableProps({
    loading: loading || loadingFato,
    result:
      data?.atividadesColetivas ||
      convertFatoAtividadeColetivaTableQueryToAtividadeColetivaTableQuery(dataFato.fatoAtividadesColetivas),
    onChange: setLocalFilter,
  })

  const renderDataAtividade = (item: AtividadeColetivaTableItem) => {
    return (
      <HFlow>
        <DateTime format='DD/MM/YYYY' value={item.dataAtividade} />
        {!item.registroEnviado && (
          <Tooltip text='Registro não processado para essa atividade'>
            <Icon icon='sync' size={1} />
          </Tooltip>
        )}
      </HFlow>
    )
  }

  const handleExcluir = (event: MouseEvent<HTMLButtonElement>, atividadeColetivaId: ID) => {
    event.stopPropagation()

    confirm({
      title: 'Deseja excluir o registro de Atividade coletiva?',
      confirmLabel: 'Excluir',
      type: 'danger',
      onConfirm: () => {
        excluirAtividadeColetiva({ variables: { atividadeColetivaId } })
          .then(() => {
            refetch({ input: filter })
            alert('success', 'Atividade coletiva excluída com sucesso.')
            analytics.logEvent(`atividade_coletiva_${filter.isReuniao ? 'reuniao' : 'saude'}_excluida`)
          })
          .catch(handleRejection)
      },
    })()
  }

  const renderActions = (item: AtividadeColetivaTableItem) => (
    <HFlow hSpacing={0} justifyContent='flex-end'>
      <CheckPermission permission={Permissions.cds.fichaDeAtividadeColetiva.cadastrarEditarEExcluir}>
        <Tooltip text={item.registroEnviado ? 'Edição desabilitada para registro já processado' : 'Editar'}>
          <ButtonLink size='small' skin='ghost' to={`${match.url}/editar/${item.id}`} disabled={item.registroEnviado}>
            <Icon icon='penOutline' />
          </ButtonLink>
        </Tooltip>
      </CheckPermission>
      <CheckPermission permission={Permissions.cds.fichaDeAtividadeColetiva.cadastrarEditarEExcluir}>
        <Tooltip text={item.registroEnviado ? 'Exclusão desabilitada para registro já processado' : 'Excluir'}>
          <Button
            size='small'
            skin='ghost'
            onClick={(event: MouseEvent<HTMLButtonElement>) => handleExcluir(event, item.id)}
            disabled={item.registroEnviado}
          >
            <Icon icon='trashOutline' />
          </Button>
        </Tooltip>
      </CheckPermission>
      <Tooltip text='Visualizar'>
        <ButtonLink
          size='small'
          skin='ghost'
          to={`${match.url}/visualizar/${isFonteDadosPec ? '' : 'fato/'}${item.id}`}
        >
          <Icon icon='zoomOutline' />
        </ButtonLink>
      </Tooltip>
    </HFlow>
  )

  return (
    <TableBox>
      <PagedTable<AtividadeColetivaTableItem>
        columns={[
          {
            name: 'dataAtividade',
            header: 'Data da atividade',
            render: renderDataAtividade,
          },
          {
            name: 'tipoAtividade',
            header: 'Tipo de atividade',
            render: (item) => <Text>{tipoAtividadeRecord[item.tipoAtividade]}</Text>,
          },
          ...(!isFonteDadosPec
            ? [
                {
                  name: 'origemAtividade',
                  header: 'Origem',
                  render: (item) => <Text style={styles.origemWrap}>{OrigemDadoEnumRecord[item.origem]?.nome}</Text>,
                },
              ]
            : []),
          {
            name: 'numeroParticipantes',
            header: 'Nº de participantes',
            render: (item) => <Text>{item.numeroParticipantes}</Text>,
          },
          {
            name: 'responsavel',
            header: 'Responsável',
            render: (item) => formatNomeProfissional(item.lotacaoResponsavel.profissional).titleCase(),
          },
          {
            name: 'actions',
            align: 'right',
            render: renderActions,
          },
        ]}
        {...tableProps}
        sizeOptions={[10, 30, 50]}
      />
    </TableBox>
  )
}

const styles = {
  tableFooter: css`
    border-top: 0;
  `,
  origemWrap: css`
    white-space: nowrap;
  `,
}
